import React, { useCallback, useEffect, useState } from "react";
import Paginator from "../components/Paginator";
import { LawCard } from "../components/LawCard";
import { useLocation, useParams } from "react-router-dom";
import Hero from "../components/Hero";
import { checksavedUser } from "../utils/session";
import { useNavigate } from "react-router-dom";
import SalesCard from "../components/SalesCard";
import DatePicker from "react-datepicker";
import State from "../api/Laws";
import { Period } from "../utils/period";
import { useGetLaws } from "../queries/law.queries";

const StateLaws = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { validSession } = checksavedUser();


  const [, setValidSession] = useState(false);
  const [validToken, setValidToken] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [allSales, setAllSales] = useState(0);

  const [individualSales, setIndividualSales] = useState(0);
  const [outrightSales, setOutrightSales] = useState(0);
  const [addonSale, setAddonSale] = useState(0);
  const [commissionSales, setCommissionSales] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(30);
  const [pageLimit] = useState(5);
  const [, setSearchTerm] = useState("");
  const [filter, setFilter] = useState(false);

  const [searchResult, setSearchResult] = useState();
  const [pageGroup, setPageGroup] = useState(
    new Array(pageLimit).fill().map((_, index) => index + 1),
  );

  const [filterLabel, setFilterLabel] = useState(Period.getPeriodRange());

  let currentLaws = [];
  const pageStop = currentPage * perPage;
  const pageStart = pageStop - perPage;


  const { data, isPending } = useGetLaws(params.id, { enabled: validSession })
  const laws = data?.laws ?? [];
  const stateName = data?.name ?? "";

  const getDashboardStats = useCallback(async (
    token = validToken,
    startDate = "",
    endDate = "",
  ) => {
    try {
      const response = await State.getDashboardStats(
        token,
        params.id,
        startDate,
        endDate,
      );

      if (response) {
        setAllSales(response?.total_grouped_sales?.total_amount ?? 0);
        setCommissionSales(response?.total_grouped_sales?.commissions ?? 0);
        setAddonSale(response?.addon_sales?.amount ?? 0);
        setOutrightSales(response?.sales_breakdown?.total_outright_sales ?? 0);
        setIndividualSales(
          response?.sales_breakdown?.total_non_outright_sales ?? 0,
        );
      }
    } catch (error) {
      console.table(error);
    }
  }, [validToken, params.id]);

  const handleSearch = (searchString) => {
    setSearchTerm(searchString);
    let match = laws.filter((law) =>
      law.name.toLowerCase().includes(searchString.toLowerCase()),
    );
    setSearchResult(match);
    currentLaws = searchResult?.slice(0, perPage);
  };

  const handleDateSearch = () => {
    setFilterLabel(Period.formatDateRange(startDate, endDate));
    getDashboardStats(
      validToken,
      Period.setDateFormat(startDate),
      Period.setDateFormat(endDate),
    );
  };

  const changePage = (page) => {
    setCurrentPage(page);
    setPageGroup(getPageGroup(page));
  };

  const getPageGroup = (page) => {
    let start = Math.floor((page - 1) / pageLimit) * pageLimit;

    return new Array(pageLimit).fill().map((_, index) => start + index + 1);
  };

  const getQuarterDates = (quarter) => {
    const year = new Date().getFullYear();
    let start = "";
    let end = "";

    switch (quarter) {
      case 1:
        start = `${year}-01-01`;
        end = `${year}-03-31`;
        break;
      case 2:
        start = `${year}-04-01`;
        end = `${year}-06-30`;
        break;
      case 3:
        start = `${year}-07-01`;
        end = `${year}-09-30`;
        break;
      case 4:
        start = `${year}-10-01`;
        end = `${year}-12-31`;
        break;
      default:
        break;
    }
    return { start, end };
  };

  const filterPeriod = (period, quarter) => {
    const { start, end } = getQuarterDates(quarter);
    getDashboardStats(validToken, start, end);
    setFilterLabel(Period.getPeriodRange(quarter));
  };

  useEffect(() => {
    //TODO:Address annual sub request for non-lagos states(s)
    const { validSession, token } = checksavedUser();

    setValidSession(validSession);
    setValidToken(token);
    if (validSession) {
      getDashboardStats(token);
    } else {
      navigate("/");
    }

    return () => { };
  }, [navigate, params.id, getDashboardStats]);

  currentLaws =
    searchResult?.length > 0
      ? searchResult.slice(pageStart, pageStop)
      : laws?.slice(pageStart, pageStop);

  return isPending ? (
    <div className="loading"></div>
  ) : (
    <>
      <Hero stateName={stateName} />
      <div className="content-wrapper">
        <div className="summary">
          <div className="summary__heading">
            <span className="summary__interval">{filterLabel}</span>
            <div className="summary__filter--options">
              <div
                className="summary__filter"
                onClick={() => setFilter(!filter)}
              >
                <div className="summary__filter--icon">
                  <span className="summary__filter--img">
                    <img src="/images/filter.png" alt="" />
                  </span>
                  <span className="summary__filter--text">Period</span>
                </div>
                <div
                  className={`summary__filter--periods ${filter ? "summary__filter--visible" : ""
                    } `}
                >
                  {[
                    { label: "First Quarter", value: "first_quarter", id: 1 },
                    { label: "Second Quarter", value: "second_quarter", id: 2 },
                    { label: "Third Quarter", value: "third_quarter", id: 3 },
                    { label: "Fourth Quarter", value: "fourth_quarter", id: 4 },
                  ].map((quarter) => (
                    <span
                      key={quarter.id}
                      className="summary__filter--period"
                      style={
                        quarter.id > Math.floor(new Date().getMonth() / 3 + 1)
                          ? {
                            opacity: 0.5,
                            cursor: "not-allowed",
                            pointerEvents: "none",
                          }
                          : {}
                      }
                      onClick={() => filterPeriod(quarter.value, quarter.id)}
                    >
                      {quarter.label}
                    </span>
                  ))}
                </div>
              </div>
              <div className="summary__filter--date">
                <div className="summary__filter--start">
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    showIcon
                    withPortal
                    showYearDropdown
                    dropdownMode="scroll"
                    maxDate={new Date()}
                  />
                </div>
                <div className="summary__filter--end">
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    showIcon
                    withPortal
                    showYearDropdown
                    dropdownMode="scroll"
                    maxDate={new Date()}
                  />
                </div>
                <button
                  className="summary__filter--search"
                  onClick={() => handleDateSearch()}
                >
                  <span className="summary__filter--text">Search</span>
                </button>
              </div>
            </div>
          </div>
          <div className="summary__cards">
            <SalesCard
              label={"Total Sales"}
              value={allSales}
              icon={"wallet"}
              color={"green"}
            />

            <SalesCard
              label={"Sales from Outright Purchase"}
              value={outrightSales}
              icon={"tag"}
              color={"orange"}
            />
            <SalesCard
              label={"Sales from Individual Laws"}
              value={individualSales}
              icon={"tag"}
              color={"orange"}
            />
            <SalesCard
              label={"Sales from Add-on Purchase"}
              value={addonSale}
              icon={"tag"}
              color={"orange"}
            />
            <SalesCard
              label={"Royalty"}
              value={commissionSales || 0}
              icon={"cost-total"}
              color={"pink"}
              child={<span className="card-label__super">45%</span>}
            />
          </div>
        </div>
        <div className="laws">
          <div className="laws__heading">
            <p className="heading-title">{`All Laws ( ${laws?.length}) `}</p>
            <div className="heading-options">
              <div className="search">
                <div className="search__desc">
                  <img
                    className="search__desc--icon"
                    src="/images/search.png"
                    alt=""
                  />
                </div>
                <input
                  id="search"
                  className="search__input"
                  type="text"
                  name="search"
                  placeholder="Search For Law..."
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="laws__container">
            {currentLaws.length ? (
              currentLaws.map((law) => (
                <LawCard
                  law={law}
                  url={`${location.pathname}/laws/${law.id}`}
                  key={law.id}
                />
              ))
            ) : (
              <div className="loading"></div>
            )}
          </div>
          {currentLaws.length && (
            <Paginator
              perPage={perPage}
              totalLaws={searchResult?.length || laws?.length}
              changePage={changePage}
              currentPage={currentPage}
              pageGroup={pageGroup}
              setPageGroup={setPageGroup}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default StateLaws;
